import { Component, OnInit } from '@angular/core';
import { App, OktaGroup, OktaUser, UserOperation } from 'src/app/shared/model/okta';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ErrorService } from 'src/app/shared/services/error.service';
import { OktaUserService } from 'src/app/shared/services/oktauser.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { OktaAuthService } from 'src/app/app.service';
import { StringMap } from '@angular/compiler/src/compiler_facade_interface';
import { ActivatedRoute } from '@angular/router';
import { OktaAuth } from '@okta/okta-auth-js';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-usermanagement',
  templateUrl: './usermanagement.component.html',
  styleUrls: ['./usermanagement.component.scss']
})
export class UserManagementComponent implements OnInit {
  // data
  groups: OktaGroup[];
  adminGroup: OktaGroup;
  userGroup: OktaGroup;
  adminUsers: OktaUser[];
  userUsers: OktaUser[];
  displayedUsers: OktaUser[];
  selectedTenantApps: App[];
  
  // view variables
  dataLoaded = false;
  selectedTab: string;
  selectedTabContext: string;

  // users
  selectedUser: any;
  selectedUserLinks: any[];
  userFilter: string;

  // use for confirm modal when running actions
  userId: string;
  operation: string;
  operationDisplay: string;
  userDisplay: string;

  // form for creating normal user
  userForm: FormGroup;
  submittedUser = false;
  activatedUserApps: any[];
  availableUserRoles: any[];
  selectedUserRoleIds: string[];
  adminGroupTargetGroups: any[];

  // editting user
  editting = false;
  edittingUser: any;

  // display
  redirecting = false;

  constructor(private oktaAuth: OktaAuthService, private oktaUserService: OktaUserService, private oktaAuthService: OktaAuthService, private errorService: ErrorService, private modalService: NgbModal, private formBuilder: FormBuilder, private route: ActivatedRoute) { }
 
  ngOnInit() {
    this.selectedTab = 'users';
    this.selectedTabContext = 'list';

    let currentTimestamp:number = Math.floor(Date.now() / 1000);

    if(localStorage.getItem("okta-mta") 
        && localStorage.getItem("okta-mta-expires") 
        && (parseInt(localStorage.getItem("okta-mta-expires")) > currentTimestamp)
        && !this.oktaAuth.oktaAuth.tokenManager.hasExpired(localStorage.getItem("okta-mta"))){

      this.setup();

    }else{

      this.redirecting = true;
      this.route.queryParams.subscribe(params => {
        if(!params['authd']){
          this.oktaAuth.storeOktaAdminInfoRedirect();
        }else{
          this.oktaAuth.oktaAuth.token.parseFromUrl()
            .then(tokenContainer => {
              localStorage.setItem("okta-mta",JSON.stringify({
                "o4otoken": tokenContainer.tokens.accessToken.accessToken
              }));

              // manual tracking of mta token expiry
              let tokenExpirationTime: number = 3600; // seconds, as set in Okta
              let timestamp = Math.floor(Date.now() / 1000) + tokenExpirationTime;
              localStorage.setItem("okta-mta-expires", timestamp.toString());
            })
            .then(() => {
              this.setup();
              this.redirecting = false;
            });
        }
      });
    }
  }

  setup(){
    this.oktaUserService.listGroups()
      .then(res => {
        this.groups = res;    

        let adminGroups =this.groups.filter(g => g.profile.name.startsWith("ADMINS_"));
        let userGroups =this.groups.filter(g => g.profile.name.startsWith("USERS_"));

        if(adminGroups.length > 1){
          this.errorService.handlerError("You appear to be an admin on multiple tenants. Please contact service management")
        }else if(adminGroups.length === 0){
          this.errorService.handlerError("You do not have permission to access user management");
        }else if(adminGroups.length === 1){
          this.adminGroup = adminGroups[0];
          this.userGroup = userGroups[0];

          this.oktaUserService.listUsersForGroup(this.adminGroup.id)
            .then(res2 => {
              this.adminUsers = res2;
            })         
            .catch(err => {
              throw err;
            })

          this.oktaUserService.listUsersForGroup(this.userGroup.id)
            .then(res2 => {
              this.userUsers = res2;
              this.filterUsers();
            })
            .catch(err => {
              throw err;
            })

          this.oktaUserService.listTenantApps()
            .then(res => {
              this.selectedTenantApps = res;
            })
            .catch(err => {
              throw err;
            });

          this.dataLoaded = true;
        }
      })
      .catch(err => {
        this.errorService.handlerError(err);
      })

      this.activatedUserApps = [];
      this.availableUserRoles = [];
      this.selectedUserRoleIds = [];
      this.userForm = this.formBuilder.group({
        firstName: ['', Validators.required],
        lastName: ['', Validators.required],
        email: ['', [Validators.required, Validators.email]]
      }, {});
  }

    get uf() { return this.userForm.controls; }
   /**
   * This is called when the dropdown list of actions is initially clicked
   * It returns the full user object including a list of links which are the
   * lifecycle options for this user
   * 
   * @param userId - user id
   */
  selectUser(userId: string) {
    this.oktaUserService.getUser(userId)
      .then(res => {
        this.selectedUser = res;
        let links = [];
        if(this.selectedUser._links.resetPassword){
          links.push(new UserOperation("Reset Password", "resetpassword"));
        }
        if(this.selectedUser._links.deactivate){
          links.push(new UserOperation("Deactivate", "deactivate"));
        }
        if(this.selectedUser._links.activate){
          links.push(new UserOperation("Activate", "activate"))
        }
        if(this.selectedUser._links.reactivate){
          links.push(new UserOperation("Reactivate", "reactivate"))
        }
        if(this.selectedUser._links.delete){
          links.push(new UserOperation("Delete", "delete"));
        }
        if(this.selectedUser._links.unlock){
          links.push(new UserOperation("Unlock", "unlock"));
        }
        if(this.selectedUser._links.suspend){
          links.push(new UserOperation("Suspend", "suspend"));
        }
        if(this.selectedUser._links.resetFactors){
          links.push(new UserOperation("Reset Factors", "resetfactors"));
        }
        this.selectedUserLinks = links;
      })
      .catch(err => {
        this.errorService.handlerError(err);
      })
  }

  filterUsers(){
    if(this.userFilter && this.userFilter.length > 0){
      this.displayedUsers = this.userUsers.filter(u => u.profile.email.includes(this.userFilter));
    }else{
      this.displayedUsers = JSON.parse(JSON.stringify(this.userUsers));
    }
  }

  sortUsers(sort: string){
    this.displayedUsers.sort(function(a, b) {
      if(sort === 'lastLogin'){
        var valA = a.lastLogin.toUpperCase(); 
        var valB = b.lastLogin.toUpperCase(); 
      }else if(sort === 'email'){
        var valA = a.profile.email.toUpperCase(); 
        var valB = b.profile.email.toUpperCase(); 
      }else if(sort === 'status'){
        var valA = a.status.toUpperCase(); 
        var valB = b.status.toUpperCase(); 
      }else{
        var valA = '';
        var valB = '';
      }
      
      if (valA < valB) {
        return -1;
      }
      if (valA > valB) {
        return 1;
      }
      return 0;
    });
  }

  /**
   * Run a lifecycle operation on the user object. This method creates the confirmation box
   * @param content - modal to use
   * @param userId - user id
   * @param operation - operation short name
   * @param userDisplay - user display name
   */
  userOperation(content, userId: string, operation: string, userDisplay: string) {
    this.userId = userId;
    this.userDisplay = userDisplay;
    this.operation = operation;
    this.selectedUserLinks.forEach(lnk => {
        if(lnk.operation === operation){
          this.operationDisplay = lnk.name;
        }
    });

    // reset operation details as soon as modal is closed
    const domModal = this.modalService.open(content, { size: 'lg' }).result
      .then((result) => {
        delete this.userId;
        delete this.operation;
        delete this.operationDisplay;
        delete this.userDisplay;
      }, (reason) => {
        delete this.userId;
        delete this.operation;
        delete this.operationDisplay;
        delete this.userDisplay;
      });
  }

  /**
   * Open modal to confirm the upgrade of a user to admin
   * @param content - modal
   * @param userId - user id
   * @param userDisplay - user email address
   */
  upgradeUserToAdmin(content, userId: string, userDisplay: string){
    this.userId = userId;
    this.userDisplay = userDisplay;
    this.operation = "upgradetoadmin";
    this.operationDisplay = "Upgrade To Admin";

    // reset operation details as soon as modal is closed
    const domModal = this.modalService.open(content, { size: 'lg' }).result
      .then((result) => {
        delete this.userId;
        delete this.operationDisplay;
        delete this.operation;
        delete this.userDisplay;
      }, (reason) => {
        delete this.userId;
        delete this.operationDisplay;
        delete this.operation;
        delete this.userDisplay;
      });
  }

  /**
   * Open modal to confirm the downgrade of a user from admin
   * @param content - modal
   * @param userId - user id
   * @param userDisplay - user email address
   */
  downgradeUserFromAdmin(content, userId: string, userDisplay: string){
    this.userId = userId;
    this.userDisplay = userDisplay;
    this.operation = "downgradefromadmin";
    this.operationDisplay = "Downgrade From Admin";

    // reset operation details as soon as modal is closed
    const domModal = this.modalService.open(content, { size: 'lg' }).result
      .then((result) => {
        delete this.userId;
        delete this.operationDisplay;
        delete this.userDisplay;
      }, (reason) => {
        delete this.userId;
        delete this.operationDisplay;
        delete this.userDisplay;
      });
  }

  /**
   * Once confirmed run the action
   * Most operations come from a list of options in the user object from Okta
   * upgradetoadmin/downgradefromadmin are exceptions to that rule
   * @param userId - user id
   * @param operation - operation name
   */
  actionUserOperation(userId: string, operation: string, operationDisplay: string, userDisplay: string){
    if(operation === 'upgradetoadmin'){
      this.oktaUserService.addExistingUserToTenantAdmin(userId)
        .then(res => {
          this.errorService.addSuccess(userDisplay + " upgraded to an admin");
          this.ngOnInit();
        })
        .catch(err => {
          this.errorService.handlerError(err);
        })
    }else if(operation === 'downgradefromadmin'){
      this.oktaUserService.removeUserFromTenantAdmin(userId)
        .then(res => {
          this.errorService.addSuccess(userDisplay + " removed as an admin");
          this.ngOnInit();
        })
        .catch(err => {
          this.errorService.handlerError(err);
        })
    }else{
      this.oktaUserService.userOperation(userId, operation)
        .then(res => {
          this.errorService.addSuccess(operationDisplay + " command completed for " + userDisplay);
          this.ngOnInit();
        })
        .catch(err => {
          this.errorService.handlerError(err);
        })
    }
  }

  assignToAllUsers(app: App){
    //assign the app to the USERS_ group
  }

    /**
   * Add this application to the new users available apps
   * @param app - Application object
   */
  activateAppForUser(app: any){
    this.activatedUserApps.push(app);
    this.getAllRoles();
  }


  /**
   * Remove this application from the new users available apps
   * Also remove any role groups that are linked to this app
   * @param app - Application object
   */
  deactivateAppForUser(app: any){
    let ind = this.activatedUserApps.indexOf(app);
    this.activatedUserApps.splice(ind, 1);

    this.availableUserRoles.forEach(aur => {
      if(aur.appName === app.name){
        let ind = this.selectedUserRoleIds.indexOf(aur.id);
        this.selectedUserRoleIds.splice(ind, 1);
      }
    });
    this.getAllRoles();
  } 



  /**
   * Get all the role groups available for the apps that have been
   * selected for the new user.
   */
  getAllRoles(){
    this.availableUserRoles = [];
    this.activatedUserApps.forEach(aua => {
      this.oktaUserService.getAppRoles(aua.id)
        .then(res => {
          res.forEach(r => {
            r.appName = aua.name;
            r.selected = false;
          })
          this.availableUserRoles = this.availableUserRoles.concat(res);
        })
        .catch(err => {
          this.errorService.handlerError(err);
        })
    });
  }


  /**
   * Add a new user to the tenant
   * If the user has been edited then only update the users groups
   * Will allow existing users to be added as users to the tenant, as well as create new
   */
  addUser(){
    if(this.editting){
      this.activatedUserApps.forEach(ua => {
        if(!this.selectedUserRoleIds.includes(ua.APPUSERS_groupId)){
          this.selectedUserRoleIds.push(ua.APPUSERS_groupId);          
        }
      })

      this.oktaUserService.updateUsersGroups(this.edittingUser.id, this.selectedUserRoleIds)
        .then(res => {
          this.errorService.addSuccess(this.edittingUser.profile.email + " has been updated");
          this.resetEdit();
        })
        .catch(err => {
          this.errorService.handlerError(err);
        });

    }else{
      this.submittedUser = true;
      if (this.userForm.invalid) {
          return;
      }
      this.userForm.value.login = this.userForm.value.email;
      // get all the activate app user groups and add them to the selected role group ids
      this.activatedUserApps.forEach(ua => {
        if(this.selectedUserRoleIds.includes(ua.APPUSERS_groupId)){
          this.selectedUserRoleIds.push(ua.APPUSERS_groupId);          
        }
      })
      this.userForm.value.groupIds = this.selectedUserRoleIds;
  
      this.oktaUserService.addTenantUser(this.userForm.value)
        .then(res => {
          this.errorService.addSuccess(this.userForm.value.email + " has been added to the tenant");
          this.resetUser();
          this.ngOnInit();
          this.selectedTab = 'users';
          this.selectedTabContext = 'list';
        })
        .catch(err => {
          this.errorService.handlerError(err);
        });
    }
  }

  /**
   * Reset after editting user
   */
  resetEdit(){
    this.activatedUserApps = [];
    this.availableUserRoles = [];
    this.selectedUserRoleIds = [];

    this.editting = false;
    delete this.edittingUser;
    this.selectedTabContext = 'list';
  }

  /**
   * Reset after creating user
   */
  resetUser(){
    this.activatedUserApps = [];
    this.availableUserRoles = [];
    this.selectedUserRoleIds = [];
    this.userForm.reset();
  }

  toggleListAdd(tab: string){
    this.resetEdit();
    this.resetUser();
    this.selectedTabContext = tab;
  }

  /**
   * Allow the editting of the a users groups
   * Uses the same form as the create user but with restrictions
   * @param userId - user id
   */
  editUser(userId: string){
    this.resetUser();

    this.oktaUserService.getUserIncGroups(userId)
      .then(res => {
        this.editting = true;
        this.edittingUser = res;

        res.groups.forEach(grp => {
          if(grp.profile.name.startsWith("APPUSERS")){
            let appId = grp.profile.name.split("_")[2];
            this.selectedTenantApps.forEach(ta => {
              if(ta.id === appId && this.activatedUserApps.indexOf(ta) === -1){
                this.activatedUserApps.push(ta);
              }
            })
          }
          if(grp.profile.name.startsWith("APPROLE")){
            let appId = grp.profile.name.split("_")[1];
            this.selectedTenantApps.forEach(ta => {
              if(ta.id === appId && this.activatedUserApps.indexOf(ta) === -1){
                this.activatedUserApps.push(ta);
              }
            })
            this.selectedUserRoleIds.push(grp.id);
          }
        });
        this.getAllRoles();
        this.selectedTabContext = 'add';
        this.selectedTab = 'users';
      })
      .catch(err => {
        this.errorService.handlerError(err);
      })
  }

  /**
   * Add the role group id to the new users group ids
   * @param event - click event
   * @param roleId - role id
   */
  selectRole(event, roleId: string){
    if(this.selectedUserRoleIds.includes(roleId)){
      let ind = this.selectedUserRoleIds.indexOf(roleId);
      this.selectedUserRoleIds.splice(ind, 1);
    }else{
      if(!this.selectedUserRoleIds.includes(roleId)){
        this.selectedUserRoleIds.push(roleId);
      }
    }
  }
}
